import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useBranch from "../../hooks/useBranch";
import { getModuleByPath } from '../../config/moduleConfig';
import {
  UtensilsCrossed,
  Clock,
  Maximize,
  Minimize,
  Wifi,
  ArrowLeft,
  LogOut,
  User,
  FilePlus,
  ChevronRight,
  CalendarCheck,
  ArrowLeftRight,
  ChevronDown
} from 'lucide-react';
import { shiftService } from '../../services/shiftService';
import { useBranding } from '../../context/BrandingContext';

export default function Header({
  onBack, // ← optional, shows back button only if provided
  beforeNewOrderContent, // Optional content shown immediately left of New Order
  onNewOrder, // ← optional, shows New Order button only if provided
  restaurantName: propRestaurantName, // Optional - if not provided, fetched from branch API
  hotItems: propHotItems, // Optional - if not provided, fetched from branch API
  showModuleIndicator = true, // Show current module indicator
  shift, // ← optional, active shift object from ShiftContext
  onEndShift, // ← optional, callback to open EndShiftModal
  onDayClose, // ← optional, callback to open DayCloseModal
}) {
  const navigate = useNavigate();
  const branding = useBranding();

  const handleResetPassword = () => {
    navigate("/profile/reset-password?call=profile");
    setShowLogoutMenu(false);
  };
  const location = useLocation();
  const isResetPasswordPage =
  location.pathname === "/admin/settings" &&
  location.search.includes("reset-password");
  const { user, logout } = useAuth();
  const { restaurantName: branchName, hotItems: branchHotItems } = useBranch();

  // Detect current module from path
  const currentModule = getModuleByPath(location.pathname);

  // Use props if provided, otherwise use branch data
  const restaurantName = propRestaurantName || branchName;
  const hotItems = propHotItems || branchHotItems || [];
  const [currentTime, setCurrentTime] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
      );
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Sync fullscreen state (user might press Esc)
  useEffect(() => {
    const handleChange = () => setIsMaximized(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  // Online/offline state

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Shift duration (live)
  const [shiftDuration, setShiftDuration] = useState('');
  useEffect(() => {
    const calcDuration = () => {
      if (!shift?.startedAt) return '';
      const start = new Date(shift.startedAt);
      const now = new Date();
      const diffMs = now - start;
      const hours = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };
    setShiftDuration(calcDuration());
    const timer = setInterval(() => setShiftDuration(calcDuration()), 30000);
    return () => clearInterval(timer);
  }, [shift?.startedAt]);

  const shiftStartTime = shift?.startedAt
    ? new Date(shift.startedAt).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  // Shift sales data (fetched live when dropdown opens)
  const [shiftSales, setShiftSales] = useState({ totalSales: 0, totalBills: 0, cashSales: 0 });
  useEffect(() => {
    if (showLogoutMenu && shift?.id) {
      shiftService.getShiftSummary(shift.id)
        .then(data => {
          setShiftSales({
            totalSales: data?.totalSales || 0,
            totalBills: data?.totalBills || 0,
            cashSales: data?.cashSales || 0,
          });
        })
        .catch(() => {});
    }
  }, [showLogoutMenu, shift?.id]);

  const formatCurrency = useCallback((amount) => {
    return '\u20B9' + (Number(amount) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showLogoutMenu && !e.target.closest('.user-menu')) {
        setShowLogoutMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLogoutMenu]);

  // Fullscreen toggle
  const toggleMaximize = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.warn('Fullscreen toggle failed', e);
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
    setShowLogoutMenu(false);
  }, [logout, navigate]);

  // Navigate to role selection
  const handleSwitchRole = useCallback(() => {
    navigate('/select-role');
    setShowLogoutMenu(false);
  }, [navigate]);

  return (
    <header className="bg-gray-900 shadow-xl">
      <div className="px-6 py-3 flex justify-between items-center">
        {/* Left Side */}
        <div className="flex items-center gap-4">
          {/* Back button only if onBack exists */}
          {onBack && (
            <button
              onClick={onBack}
              className="bg-slate-700 hover:bg-slate-600 p-2 rounded-lg transition shadow-md text-white flex items-center"
              title="Back"
            >
              <ArrowLeft size={18} />
            </button>
          )}

          {/* Logo - Clickable to go home */}
          {/* Logo - Hide on Reset Password page */}
        {!isResetPasswordPage && (
          <button
            onClick={() => navigate('/select-role')}
            className="bg-red-700 p-2 rounded-lg shadow-2xl flex items-center justify-center hover:bg-red-600 transition-colors"
            style={{ boxShadow: '0 0 10px rgba(185, 28, 28, 0.6)' }}
            title="Go to Home"
          >
            <UtensilsCrossed size={22} className="text-white" />
          </button>
)}

          {/* Title & Hot Items */}
          <div className="flex-1 min-w-0">
            <h1
              className="text-2xl font-black text-red-600 tracking-tighter leading-tight truncate"
              style={{ letterSpacing: '0.05em' }}
            >
              {restaurantName}
            </h1>
            {hotItems?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {hotItems.map((item, i) => {
                  // Tailwind gradient color palette
                  const colorClasses = [
                    'bg-purple-600',
                    'bg-gray-700',
                    'bg-cyan-500',
                    'bg-emerald-500',
                    'bg-orange-500',
                    'bg-rose-500',
                    'bg-indigo-500',
                    'bg-teal-500'
                  ];
                  const colorClass = colorClasses[i % colorClasses.length];

                  return (
                    <span
                      key={i}
                      className={`${colorClass} text-white px-3 py-0.5 text-xs font-black rounded shadow-sm transition-transform transform hover:scale-105`}
                      style={{ opacity: 0.95 }}
                    >
                      {item}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Current Module Indicator */}
          {showModuleIndicator && currentModule && (
            <div className="flex items-center gap-2 pl-4 border-l border-white/20">
              <ChevronRight size={14} className="text-gray-500" />
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r ${currentModule.gradient} shadow-lg`}>
                {(() => {
                  const ModuleIcon = currentModule.icon;
                  return <ModuleIcon size={16} className="text-white" />;
                })()}
                <span className="text-sm font-bold text-white">{currentModule.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4">
            {beforeNewOrderContent}
            {onNewOrder && (
                        <button
                          onClick={onNewOrder}
                          className="px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 rounded-lg transition flex items-center gap-2 border border-white/10"
                          title="Save current order and select a new table (F7)"
                        >
                          <FilePlus size={16} />
                          <span className="text-xs font-medium">New Order</span>
                          <span className="text-[10px] font-bold bg-purple-800 text-purple-100 px-1.5 py-0.5 rounded">F7</span>
                        </button>
                      )}
          {/* Online indicator */}
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 ${
              online ? 'bg-green-600/10 text-green-300' : 'bg-rose-600/10 text-rose-300'
            }`}
            title={online ? 'Online' : 'Offline'}
          >
            <Wifi size={16} />
            <span className="text-xs font-medium">{online ? 'Online' : 'Offline'}</span>
          </div>

          {/* Clock */}
          <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/10">
            <Clock size={16} className="text-slate-300" />
            <span className="font-bold text-white font-mono text-xs">{currentTime}</span>
          </div>

          {/* New Order Button - only if onNewOrder exists */}


          {/* Fullscreen toggle */}
          <button
            onClick={toggleMaximize}
            className={`px-3 py-2 rounded-lg transition border border-white/10 flex items-center ${
              isMaximized
                ? 'bg-red-600/10 text-red-300 hover:bg-red-600/20'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            title={isMaximized ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isMaximized ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>

          {/* A Product by Maverick Ignite */}
          <a
            href={branding.companyWebsite}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/10 hover:bg-white/20 transition"
            title={`A Product by ${branding.companyName}`}
          >
            <span className="text-[10px] text-gray-400">A Product by</span>
            <img
              src={branding.companyLogoWhiteUrl}
              alt={branding.companyName}
              className="h-4 object-contain"
            />
          </a>

          {/* Unified User Menu */}
          <div className="relative user-menu">
            <button
              onClick={() => setShowLogoutMenu(!showLogoutMenu)}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/10 text-white hover:bg-white/20 transition"
              title={`Logged in as ${user?.name || 'User'}`}
            >
              <div className="relative">
                <User size={16} />
                {shift && (
                  <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                )}
              </div>
              <span className="text-xs font-medium hidden sm:inline">
                {user?.name || 'User'}
              </span>
              <ChevronDown size={12} className={`transition-transform ${showLogoutMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Unified Dropdown */}
            {showLogoutMenu && (
              <div className="absolute right-0 mt-2 w-80 rounded-2xl overflow-hidden z-50 animate-fadeIn shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)]">

                {/* User Card */}
                <div className="px-5 py-4 bg-teal-600">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-white font-bold text-sm">
                        {(user?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-teal-100 capitalize">
                        {user?.role === 'both' ? 'Manager' : user?.role || 'Staff'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Shift Section — only when shift is active */}
                {shift && (
                  <div className="p-4 bg-slate-50 border-b-2 border-slate-200">
                    {/* Status row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                        </span>
                        <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">
                          Active
                        </span>
                      </div>
                      <span className="text-[11px] text-slate-400 font-medium font-mono">
                        {shiftStartTime} &middot; {shiftDuration}
                      </span>
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-white rounded-xl p-2.5 text-center border border-slate-100 shadow-sm">
                        <p className="text-sm font-bold text-slate-800 truncate">
                          {formatCurrency(shiftSales.totalSales)}
                        </p>
                        <p className="text-[10px] text-slate-400 uppercase font-medium mt-0.5">Sales</p>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 text-center border border-slate-100 shadow-sm">
                        <p className="text-sm font-bold text-slate-800">
                          {shiftSales.totalBills || 0}
                        </p>
                        <p className="text-[10px] text-slate-400 uppercase font-medium mt-0.5">Orders</p>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 text-center border border-slate-100 shadow-sm">
                        <p className="text-sm font-bold text-emerald-600 truncate">
                          {formatCurrency(shiftSales.cashSales)}
                        </p>
                        <p className="text-[10px] text-slate-400 uppercase font-medium mt-0.5">Cash</p>
                      </div>
                    </div>

                    {/* Shift action buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setShowLogoutMenu(false); onEndShift?.(); }}
                        className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-lg transition flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        <Clock size={13} />
                        End Shift
                      </button>
                      {onDayClose && (
                        <button
                          onClick={() => { setShowLogoutMenu(false); onDayClose(); }}
                          className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          <CalendarCheck size={13} />
                          Close Day
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation Actions */}
                <div className="bg-white">
                  <button
                    onClick={handleSwitchRole}
                    className="w-full px-5 py-3 text-left hover:bg-slate-50 transition-colors flex items-center gap-3 text-slate-600"
                  >
                    <ArrowLeftRight size={16} className="text-slate-400" />
                    <span className="text-sm font-medium">Switch Portal</span>
                  </button>
                  <button
                    onClick={() => handleResetPassword("profile")}
                    className="w-full px-5 py-3 text-left hover:bg-slate-50 transition-colors flex items-center gap-3"
                  >
                    <User size={16} className="text-slate-400" />
                    <span className="text-sm font-medium">Reset Password</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-5 py-3 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-red-500 border-t border-slate-100"
                  >
                    <LogOut size={16} />
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add CSS animation for dropdown */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </header>
  );
}